import React, { useEffect, useState } from 'react';
import { boolean, object, string } from 'yup';
import { useTranslation, Trans } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useField } from 'formik';

import { FontAwesomeIcon as FA } from '@fortawesome/react-fontawesome';
import { faUser } from '@fortawesome/free-solid-svg-icons/faUser';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons/faEnvelope';
import { faKey } from '@fortawesome/free-solid-svg-icons/faKey';
import { faSchool } from '@fortawesome/free-solid-svg-icons/faSchool';
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons/faExclamationCircle';

import Form from '~/commons/components/form';
import {
  username,
  email,
  password,
  studentID,
  mobile,
} from '~/commons/validators';
import useHttp from '~/commons/hooks/useHttp';
import { faPhone } from '@fortawesome/free-solid-svg-icons';
import Page from '../../layout/Page';
import Card from '../../layout/Card';
import PageMascots from '../PageMascots';

const UserExists = () => {
  const { t } = useTranslation();
  const [{ value }, , { error }] = useField('email');
  const {
    req,
    res: { payload },
  } = useHttp();
  const { exists } = payload || {};

  useEffect(() => {
    if (value && !error) {
      req({
        url: '/api/users/exists',
        method: 'post',
        data: { email: value },
      });
    }
  }, [value, !!error]);

  if (!exists) return null;

  return (
    <div className="mb-3 mt-n2 small text-danger">
      <FA icon={faExclamationCircle} className="me-2" />
      {t('pg.register.userExists')}
    </div>
  );
};

const EssentialFields = ({ children }) => {
  const { t } = useTranslation();

  return (
    <Page>
      <Card grid="sm">
        <h3>
          <strong>{t('pg.register.title')}</strong>
        </h3>
        <Form.Input name="username" labelIcon={faUser} lineStyle />
        <Form.Input name="studentID" labelIcon={faUser} lineStyle />
        <Form.Input name="school" labelIcon={faSchool} lineStyle />
        <Form.Input name="email" labelIcon={faEnvelope} lineStyle />
        <UserExists />
        <Form.Password name="password" labelIcon={faKey} lineStyle />

        <Form.Input name="mobile" labelIcon={faPhone} lineStyle />

        <Form.OtpAuth
          name="verifyKey"
          data={(v) => ({ mobile: v.mobile })}
          lineStyle
        >
          {t('pg.auth.otpAuth')}
        </Form.OtpAuth>

        <div className="mb-3">
          <Link to="/auth">
            <strong>{t('pg.register.login')}</strong>
          </Link>
        </div>

        <div className="mb-3">
          <Form.Check
            name="pics"
            label={(
              <Trans i18nKey="pg.register.pics">
                I agree to the&nbsp;
                <Link to="/pics" target="_blank">
                  PICS
                </Link>
                .
              </Trans>
            )}
          />
        </div>
        <div className="mb-3">
          <Form.Check
            name="marketing"
            label={t('pg.register.marketing')}
          />
        </div>

        {children}
      </Card>
    </Page>
  );
};

const MascotField = ({ children }) => {
  const { t } = useTranslation();
  const [, , { setValue }] = useField('mascot');

  return (
    <PageMascots title={t('pg.register.mascot')} onChange={(m) => setValue(m)}>
      {children}
    </PageMascots>
  );
};

const PageRegister = () => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  console.log(i18n.language);
  return (
    <Form
      alert={{ className: 'w-auto my-3' }}
      schema={object({
        username: username().required(),
        studentID: studentID().required(),
        school: string().required(),
        email: email().required(),
        password: password().required(),
        mobile: mobile().required(),
        verifyKey: string().required(),
        pics: boolean().test('pics', 'You must agree to the PICS', (value) => value === true),
        marketing: boolean(),
      })}
      defaults={{
        username: '',
        studentID: '',
        email: '',
        password: '',
        school: '',
        mascot: null,
        lang: i18n.language,
        mobile: '',
        verifyKey: '',
        marketing: false,
        pics: false,
      }}
      api={{ url: '/api/otps/register-email' }}
      onSubmitted={() => navigate('sent')}
    >
      {({ isValid, dirty }) => {
        console.log({ isValid, dirty });
        return step === 0 ? (
          <EssentialFields>
            <button
              type="button"
              className="btn btn-rock-3 fontBorder"
              onClick={() => setStep(1)}
              disabled={!isValid || !dirty}
            >
              {t('next', 'Next')}
            </button>
          </EssentialFields>
        ) : (
          <MascotField>
            <div className="text-center">
              <Form.BtnSubmit icon={null} className="btn-rock-3 fontBorder">
                {t('pg.register.submit')}
              </Form.BtnSubmit>
            </div>
          </MascotField>
        );
      }}
    </Form>
  );
};

export default PageRegister;
